import React, { useRef, useState } from 'react';
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from 'react-places-autocomplete';
import { useTranslation } from 'react-i18next';
import { useGoogleMapsApi } from '../../../../../GoogleMapsApiContext';
import { LatLng } from '../../../../utils/types';
import { distanceMeters } from '../../../../utils/geo';
import { placesSearchOptions } from '../../../../utils/placesBias';
import {
  GeolocationFailure,
  getCurrentPosition,
  toGeolocationFailure,
} from '../../../../utils/geolocation';
import { ClearIcon, LocationArrowIcon } from '../icons';
import {
  buildingExistsAtCoord,
  reverseGeocodeNearestAddress,
  ReverseGeocodedAddress,
} from '../../../../HouseMap/BuildingMap.helpers';

// A precise civic address has a building-level type, or a route + house number
// in its components. Used to require a house number before advancing.
const isPreciseAddress = (result: {
  types?: string[];
  address_components?: Array<{ types: string[] }>;
}): boolean => {
  const types = result.types ?? [];
  if (
    types.includes('street_address') ||
    types.includes('premise') ||
    types.includes('subpremise')
  ) {
    return true;
  }
  const components = result.address_components ?? [];
  const hasNumber = components.some((c) => c.types.includes('street_number'));
  const hasRoute = components.some((c) => c.types.includes('route'));
  return hasNumber && hasRoute;
};

// Sanity bound (~2 km) between the snapped geocode result and the raw device
// position; beyond this we treat the forward geocode as the wrong place (e.g. an
// ambiguous street name in another city) and keep the closer fallback instead.
const SNAP_SANITY_METERS = 2000;

interface AddressStepProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (
    value: string,
    center?: LatLng,
    postal?: string,
    requiresManualBuildingSelection?: boolean,
  ) => void;
  // Reports the input focused/typing state up to the flow so the shared map
  // (a sibling of this step, no longer a descendant) can fade out in sync.
  onFocusedChange?: (focused: boolean) => void;
  // Where to bias address predictions. The flow's live map centre, so nearby
  // streets outrank same-named ones abroad (JAA-365).
  searchCenter?: LatLng | null;
}

// Address entry chrome over a live, display-only map owned by the flow (the
// shared FlowMap). Uses react-places-autocomplete (same library as the global
// SearchInput) when Google Maps is loaded, else a plain text input.
const AddressStep = ({
  value,
  onChange,
  onSelect,
  onFocusedChange,
  searchCenter,
}: AddressStepProps) => {
  const { t } = useTranslation();
  const { isLoaded } = useGoogleMapsApi();
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Focused/typing state: collapses the title + label under the header, fades
  // the map out, and reveals the "use current location" button (Figma).
  const [isFocused, setIsFocused] = useState(false);
  // Inline blocker shown under the field when the address has no house number
  // ('houseNumber') or when Google could only partially match it ('notFound',
  // e.g. an invented house number that interpolates to a non-existent point).
  const [error, setError] = useState<null | 'houseNumber' | 'notFound'>(null);
  // True while the hidden building-existence check runs after a valid pick.
  const [isChecking, setIsChecking] = useState(false);
  // Bumped on every pick so a slow check from a superseded address is ignored.
  const selectTokenRef = useRef(0);
  // Geolocation state: `isLocating` while the device position + reverse geocode
  // resolve, `locationResult` is the resolved "Your location" row pinned to the
  // top of the results, and `locationError` shows why geolocation failed.
  const [isLocating, setIsLocating] = useState(false);
  const [locationResult, setLocationResult] = useState<{
    label: string;
    latLng: LatLng;
    postal: string;
  } | null>(null);
  const [locationError, setLocationError] = useState<GeolocationFailure | null>(
    null,
  );

  // Keep the local flag (drives this step's own intro/input/cta animations) and
  // the lifted flag (drives the shared map fade) in sync.
  const updateFocused = (focused: boolean) => {
    setIsFocused(focused);
    onFocusedChange?.(focused);
  };

  // Only leave the focused state when focus moves outside the input region
  // entirely (so the clear button, suggestions, and current-location button
  // don't collapse the layout). Scoped to `amp-address__top` so focusing the
  // map's own controls (e.g. the Mapbox attribution toggle) never triggers the
  // input-focused state.
  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      updateFocused(false);
    }
  };

  const placeholder = t('newLandingPage.addMyPlace.address.placeholder');

  // Clear the error as soon as the user edits the input again.
  const handleChange = (next: string) => {
    if (error) setError(null);
    onChange(next);
  };

  // Geocode the chosen suggestion and require a house number before advancing.
  // If Mapbox does not have a building at the precise address yet, the building
  // step switches into manual selection mode instead of blocking here.
  const handleSelect = async (selected: string) => {
    onChange(selected);
    setError(null);
    const token = (selectTokenRef.current += 1);
    let latLng: LatLng | undefined;
    let postal = '';
    // True when Google could only interpolate the point (a non-existent or
    // unverified house number), so the pin can't be trusted as the user's
    // building and we route into manual building selection instead.
    let isInterpolated = false;
    try {
      const result = (await geocodeByAddress(selected))?.[0];
      if (selectTokenRef.current !== token) return;
      // Google flags an interpolated result for a non-existent house number with
      // `partial_match` (street matched, number did not). Reject it before the
      // precise-address gate so we never advance with an invented pin.
      if (result?.partial_match) {
        setError('notFound');
        inputRef.current?.focus();
        return;
      }
      if (result && isPreciseAddress(result)) {
        latLng = await getLatLng(result);
        postal =
          result.address_components?.find((component) =>
            component.types.includes('postal_code'),
          )?.long_name ?? '';
        // A real civic point is ROOFTOP. An invented/unverified house number
        // comes back interpolated/approximate, so we can't trust the point as
        // the user's building; require a manual pick on the map instead of
        // auto-accepting. Missing/unknown types default to not-interpolated so
        // we never over-trigger.
        const locationType = result.geometry?.location_type as
          | string
          | undefined;
        isInterpolated = !!locationType && locationType !== 'ROOFTOP';
      }
    } catch {
      // fall through to the "needs house number" hint
    }
    if (selectTokenRef.current !== token) return;
    if (!latLng) {
      setError('houseNumber');
      inputRef.current?.focus();
      return;
    }

    // Interpolated/approximate point: skip the building auto-match and drop into
    // manual selection so the user confirms (or finds) the right building.
    if (isInterpolated) {
      onSelect(selected, latLng, postal, true);
      return;
    }

    setIsChecking(true);
    const hasBuilding = await buildingExistsAtCoord(latLng);
    if (selectTokenRef.current !== token) return;
    setIsChecking(false);
    onSelect(selected, latLng, postal, !hasBuilding);
  };

  // Resolve building-snapped coordinates for a reverse-geocoded address,
  // mirroring the manual typing path (`handleSelect`) so a geolocation that
  // resolves to an address finds the same Mapbox building. Tries Google forward
  // geocoding first (parity with manual typing), then the Mapbox feature center
  // carried on the reverse-geocode result, then the raw device coords.
  const resolveSnappedLatLng = async (
    addr: ReverseGeocodedAddress,
    deviceLatLng: LatLng,
  ): Promise<LatLng> => {
    if (isLoaded) {
      const query = [
        [addr.street, addr.houseNumber].filter(Boolean).join(' ').trim(),
        [addr.postcode, addr.city].filter(Boolean).join(' ').trim(),
        addr.country,
      ]
        .filter(Boolean)
        .join(', ');
      if (query) {
        try {
          const result = (await geocodeByAddress(query))?.[0];
          if (result) {
            const snapped = await getLatLng(result);
            if (distanceMeters(snapped, deviceLatLng) <= SNAP_SANITY_METERS) {
              return snapped;
            }
          }
        } catch {
          // fall through to the Mapbox feature center / device coords
        }
      }
    }
    // `addr.coordinates` is the Mapbox feature center (snapped to the address
    // point) or the device coords when the feature had no center.
    return addr.coordinates;
  };

  // Read the device's real location, reverse-geocode it to a civic address, and
  // surface it as the pinned "Your location" row at the top of the results.
  const handleUseCurrentLocation = async () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setLocationError('unavailable');
      return;
    }
    setLocationError(null);
    setLocationResult(null);
    setError(null);
    setIsLocating(true);
    let latLng: LatLng;
    try {
      latLng = await getCurrentPosition();
    } catch (err) {
      setIsLocating(false);
      setLocationError(toGeolocationFailure(err));
      return;
    }
    const addr = await reverseGeocodeNearestAddress(latLng);
    const label = addr
      ? [addr.street, addr.houseNumber].filter(Boolean).join(' ').trim() ||
        addr.city
      : '';
    setIsLocating(false);
    if (!label || !addr) {
      setLocationError('unavailable');
      return;
    }
    // The building check (and downstream map center) must use the same
    // building-snapped coordinates the manual typing path produces, so an
    // address geolocation resolves to also passes the Mapbox building gate.
    // Forward-geocode the resolved civic address through Google (same calls
    // as `handleSelect`); fall back to the Mapbox feature center, then the
    // raw device coords.
    const resolvedLatLng = await resolveSnappedLatLng(addr, latLng);
    setLocationResult({
      label,
      latLng: resolvedLatLng,
      postal: addr.postcode ?? '',
    });
  };

  // Pick the geolocated address: we already have precise coords, so skip the
  // house-number geocode and just verify a Mapbox building exists before
  // advancing (mirrors the tail of `handleSelect`).
  const handleSelectCurrentLocation = async () => {
    if (!locationResult) return;
    const { label, latLng, postal } = locationResult;
    onChange(label);
    setError(null);
    const token = (selectTokenRef.current += 1);
    setIsChecking(true);
    const hasBuilding = await buildingExistsAtCoord(latLng);
    if (selectTokenRef.current !== token) return;
    setIsChecking(false);
    onSelect(label, latLng, postal, !hasBuilding);
  };

  const renderField = (
    inputProps: React.InputHTMLAttributes<HTMLInputElement> & {
      ref?: React.Ref<HTMLInputElement>;
    },
    autocompleteResults?: React.ReactNode,
  ) => {
    const showResults = isLocating || !!locationResult || !!autocompleteResults;
    return (
      <div className="amp-address__column">
        <div className="amp-address__field">
          <input
            {...inputProps}
            id="amp-address-input"
            className="amp-address__input"
            placeholder={placeholder}
            aria-label={t('newLandingPage.addMyPlace.address.question')}
          />
          {value && (
            <button
              type="button"
              className="amp-address__clear"
              onClick={() => {
                handleChange('');
                inputRef.current?.focus();
              }}
              aria-label={t('newLandingPage.explore.clearSearchAria')}
            >
              <ClearIcon />
            </button>
          )}
        </div>
        {showResults && (
          <div className="amp-address__results">
            {isLocating && (
              <div className="amp-address__result amp-address__result--loading">
                {t('newLandingPage.addMyPlace.address.locating')}
              </div>
            )}
            {locationResult && (
              <button
                type="button"
                className="amp-address__result amp-address__result--location"
                onClick={handleSelectCurrentLocation}
              >
                <LocationArrowIcon />
                <span>{locationResult.label}</span>
              </button>
            )}
            {autocompleteResults}
          </div>
        )}
        {isChecking && (
          <p className="amp-address__checking">
            {t('newLandingPage.addMyPlace.address.checkingBuilding')}
          </p>
        )}
        {error && (
          <p className="amp-address__error" role="alert">
            {t(
              error === 'notFound'
                ? 'newLandingPage.addMyPlace.address.addressNotFound'
                : 'newLandingPage.addMyPlace.address.houseNumberRequired',
            )}
          </p>
        )}
        {locationError && (
          <p className="amp-address__error" role="alert">
            {t(
              locationError === 'denied'
                ? 'newLandingPage.addMyPlace.address.locationDenied'
                : 'newLandingPage.addMyPlace.address.locationUnavailable',
            )}
          </p>
        )}
      </div>
    );
  };

  return (
    <div
      className="amp-step amp-step--address"
      data-address-focused={isFocused ? 'true' : 'false'}
    >
      <div
        className="amp-address__top"
        onFocus={() => updateFocused(true)}
        onBlur={handleBlur}
      >
        <div className="amp-address__intro">
          <div className="amp-address__intro-inner">
            <h2 className="amp-step__question">
              {t('newLandingPage.addMyPlace.address.question')}
            </h2>

            <label className="amp-address__label" htmlFor="amp-address-input">
              {t('newLandingPage.addMyPlace.address.label')}
            </label>
          </div>
        </div>

        {isLoaded ? (
          <PlacesAutocomplete
            value={value}
            onChange={handleChange}
            onSelect={handleSelect}
            searchOptions={placesSearchOptions(searchCenter)}
          >
            {({
              getInputProps,
              suggestions,
              getSuggestionItemProps,
              loading,
            }) => {
              const hasResults = loading || suggestions.length > 0;
              return renderField(
                getInputProps({
                  ref: (node: HTMLInputElement | null) => {
                    inputRef.current = node;
                  },
                }) as React.InputHTMLAttributes<HTMLInputElement>,
                hasResults ? (
                  <>
                    {loading && (
                      <div className="amp-address__result amp-address__result--loading">
                        {t('newLandingPage.addMyPlace.address.loading')}
                      </div>
                    )}
                    {suggestions.map((suggestion) => {
                      const props = getSuggestionItemProps(suggestion, {
                        className: `amp-address__result${suggestion.active ? ' amp-address__result--active' : ''}`,
                      });
                      return (
                        <div key={suggestion.placeId} {...props}>
                          {suggestion.description}
                        </div>
                      );
                    })}
                  </>
                ) : null,
              );
            }}
          </PlacesAutocomplete>
        ) : (
          renderField({
            value,
            onChange: (event) => handleChange(event.target.value),
            onKeyDown: (event) => {
              if (event.key === 'Enter' && value.trim()) handleSelect(value);
            },
            ref: (node: HTMLInputElement | null) => {
              inputRef.current = node;
            },
          })
        )}

        <div className="amp-address__cta">
          <div className="amp-address__cta-inner">
            <button
              type="button"
              className="amp-address__current-location"
              onClick={handleUseCurrentLocation}
              disabled={isLocating}
            >
              <LocationArrowIcon />
              <span>
                {t('newLandingPage.addMyPlace.address.useCurrentLocation')}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressStep;
